import React, { useState } from "react";
import { printingApi } from "../../lib/printing/printingApi";
import { formatWhen, operatorMessage } from "../../lib/printing/printingModel";
import { SectionCard, Notice, Pill, Modal, btnPrimary, btnSecondary, btnDanger } from "./PrintingUi";

/**
 * Phase 6 — the one operator control for automatic customer receipts.
 *
 * Two independent switches decide whether a paid order prints a receipt
 * by itself: the SERVER master gate (an environment variable operations
 * sets; read-only here) and THIS business's activation time. Enabling
 * here stamps the activation time with the server's clock — the browser
 * never sends a timestamp, so nothing can be backdated and no order paid
 * before this moment can ever print. Disabling clears it. Neither action
 * creates, retries or touches a print job.
 */
export default function AutomaticReceiptsPanel({ status, isAdmin, onChanged, notify }) {
  const [confirm, setConfirm] = useState(null); // "enable" | "disable" | null
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const known = Boolean(status && typeof status === "object");
  const gateOn = known && status.master_gate_enabled === true;
  const tenant = (known && status.tenant) || {};
  const active = tenant.active === true;
  const effective = gateOn && active;

  const perform = async (action) => {
    setBusy(true); setError(null);
    try {
      const res = await printingApi.setAutomaticReceipts(action);
      setConfirm(null);
      const now = res?.automatic_printing;
      if (action === "enable") {
        notify("success", now?.effective
          ? "Automatic receipts are enabled. Orders paid from now on will print a receipt."
          : "Automatic receipts are enabled for this business, but the server master switch is off, so nothing prints yet.");
      } else {
        notify("success", "Automatic receipts are disabled for this business.");
      }
      if (onChanged) await onChanged();
    } catch (e) {
      setError(operatorMessage(e, "Could not change automatic receipts. Nothing was changed."));
    } finally {
      setBusy(false);
    }
  };

  const closeConfirm = () => { if (!busy) { setConfirm(null); setError(null); } };

  return (
    <SectionCard
      title="Automatic customer receipts"
      description="Prints one customer receipt automatically when an order's payment is confirmed (card paid or cash received). Two switches must both be on."
      testId="auto-receipts-panel"
      actions={isAdmin && known ? (
        active
          ? <button type="button" className={btnDanger} onClick={() => setConfirm("disable")} disabled={busy} data-testid="auto-receipts-disable">Disable automatic receipts</button>
          : <button type="button" className={btnPrimary} onClick={() => setConfirm("enable")} disabled={busy} data-testid="auto-receipts-enable">Enable automatic receipts from now</button>
      ) : null}
    >
      {!known && <Notice tone="info" testId="auto-receipts-unknown">The server did not report the automatic-receipt status. Refresh to try again.</Notice>}
      {known && (
        <>
          <dl className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <div className="rounded-md border border-stone-200 bg-stone-50 p-3">
              <dt className="text-xs uppercase tracking-wide text-stone-600">Server master switch</dt>
              <dd className="mt-1 flex flex-wrap items-center gap-2">
                <Pill tone={gateOn ? "success" : "neutral"} testId="auto-receipts-master-gate">{gateOn ? "ON" : "OFF"}</Pill>
                <span className="text-xs text-stone-600">Set by operations on the server. This page cannot change it.</span>
              </dd>
            </div>
            <div className="rounded-md border border-stone-200 bg-stone-50 p-3">
              <dt className="text-xs uppercase tracking-wide text-stone-600">This business</dt>
              <dd className="mt-1 flex flex-wrap items-center gap-2">
                <Pill tone={active ? "success" : "neutral"} testId="auto-receipts-tenant-status">{active ? "Active" : "Inactive"}</Pill>
                {active && tenant.auto_print_since && (
                  <span className="text-xs text-stone-600" data-testid="auto-receipts-since">Enabled {formatWhen(tenant.auto_print_since)}. Only orders paid after that moment qualify.</span>
                )}
                {!active && <span className="text-xs text-stone-600">No activation time is set, so no receipt prints automatically.</span>}
              </dd>
            </div>
          </dl>
          <Notice tone={effective ? "success" : "warning"} testId="auto-receipts-effective" role="status">
            {effective && "Automatic receipts are live for this business: each newly confirmed payment prints one receipt."}
            {!effective && active && !gateOn && "This business is enabled, but the server master switch is OFF, so nothing prints automatically. Enabling here does not override the server switch."}
            {!effective && !active && gateOn && "The server master switch is ON, but automatic receipts are not enabled for this business, so nothing prints automatically."}
            {!effective && !active && !gateOn && "Automatic receipts are off: both the server master switch and this business's activation are off."}
          </Notice>
          {!isAdmin && <p className="text-xs text-stone-600" data-testid="auto-receipts-read-only">Only a business administrator can enable or disable automatic receipts.</p>}
        </>
      )}

      <Modal
        open={confirm === "enable"}
        title="Enable automatic receipts from now?"
        description="The activation time is set by the server clock at the moment you confirm. Orders paid before that moment never print automatically; there is no backfill."
        onClose={closeConfirm}
        testId="auto-receipts-confirm-enable"
      >
        <ul className="list-disc space-y-1 pl-5 text-sm text-stone-800">
          <li>Applies only to this business.</li>
          <li>Prints one customer receipt per confirmed payment to the configured receipt printer.</li>
          <li>{gateOn ? "The server master switch is ON, so printing starts with the next confirmed payment." : "The server master switch is OFF, so nothing prints until operations turn it on."}</li>
          <li>Enabling does not print anything by itself.</li>
        </ul>
        {error && <Notice tone="danger" testId="auto-receipts-error">{error}</Notice>}
        <div className="mt-4 flex flex-wrap justify-end gap-2">
          <button type="button" className={btnSecondary} onClick={closeConfirm} disabled={busy} data-testid="auto-receipts-cancel">Cancel</button>
          <button type="button" className={btnPrimary} onClick={() => perform("enable")} disabled={busy} data-testid="auto-receipts-confirm">{busy ? "Enabling…" : "Enable from now"}</button>
        </div>
      </Modal>

      <Modal
        open={confirm === "disable"}
        title="Disable automatic receipts?"
        description="Clears this business's activation time. Payments confirmed afterwards will not print a receipt automatically. Jobs already queued are not affected."
        onClose={closeConfirm}
        testId="auto-receipts-confirm-disable"
      >
        {error && <Notice tone="danger" testId="auto-receipts-error">{error}</Notice>}
        <div className="mt-4 flex flex-wrap justify-end gap-2">
          <button type="button" className={btnSecondary} onClick={closeConfirm} disabled={busy} data-testid="auto-receipts-cancel">Cancel</button>
          <button type="button" className={btnDanger} onClick={() => perform("disable")} disabled={busy} data-testid="auto-receipts-confirm">{busy ? "Disabling…" : "Disable"}</button>
        </div>
      </Modal>
    </SectionCard>
  );
}
